import DataMap from 'assets/components/dataMap/DataMap';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { formatNumberWithSeparator } from 'assets/utils/data/Number';
import { sumBy } from 'lodash';
import ClaimJob from 'models/claims/claimJob/Model.ClaimJob';
import ClaimJobItemPayee from 'models/claims/claimJobItemPayee/Model.ClaimJobItemPayee';
import PaymentType from 'models/enums/PaymentType';
import { useClaimJobEdit } from '../ClaimEdit.Job.Hooks';

export type ClaimJobAggregateItem = {
  key: string;
  jobItemId: number;
  jobId: number;
  payee: ClaimJobItemPayee;
  contactEmail: string;
  jobItemTypeCode: string;
  currency: string;
  paymentMethodCode: string;
  parts: number;
  labour: number;
  other: number;
  deduct: number;
  readonly ttl: number;
  gstHst: number;
  provincialSalesTax: number;
  readonly total: number;
  pendingPaymentAmount: number;
  paidAmount: number;
  usBankVirtualCardId: string;
} & { claimInvoiceId: number };

type Props = {
  aggregates: ReturnType<typeof ClaimJob.aggregates>;
  currency: string;
  onUpdate(data: Utils.FormData<Model.IClaimJobItem>): void;
  reload?(): void;
};
export default function ClaimEditJobAggregateTable({ aggregates, currency, onUpdate }: Props) {
  const { lang, translate } = getLocales();
  const { isAnyClaimJobItemPaid, isPaymentMethodLocked, permissions, isClaimAdmin, isClaimOwner } = useClaimJobEdit();

  return (
    <>
      <h4>{lang.payees}</h4>
      <table className={ThemeTableSimple} style={{ gridColumn: '-1/1', fontSize: Theme.FontSize.S }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'center', paddingLeft: '15px' }}>#</th>
            <th style={{ textAlign: 'center' }}>{lang.payee}</th>
            <th style={{ textAlign: 'center' }}>{lang.currency}</th>
            <th style={{ textAlign: 'center' }}>{lang.paymentMethod}</th>
            <th style={{ textAlign: 'center' }}>{lang.parts}</th>
            <th style={{ textAlign: 'center' }}>{lang.labour}</th>
            <th style={{ textAlign: 'center' }}>{lang.other}</th>
            <th style={{ textAlign: 'center' }}>{lang.ttl}</th>
            <th style={{ textAlign: 'center' }}>{currency === 'CAD' ? lang.gstHst : lang.tax}</th>
            {currency === 'CAD' && <th style={{ textAlign: 'center' }}>{lang.pst}</th>}
            <th style={{ textAlign: 'center' }}>{lang.totalTax}</th>
            <th style={{ textAlign: 'center' }}>{lang.deduct}</th>
            <th style={{ textAlign: 'center' }}>{lang.total}</th>
            <th style={{ textAlign: 'center', padding: '12px 3px' }}>{lang.paidAmount}</th>
          </tr>
        </thead>
        <tbody>
          <DataMap
            data={aggregates.filter((it) => it.payee?.payeeId || it.payee?.dealerId || it.payee?.clientId)}
            render={({ data: item, index }) => {
              return (
                <tr key={index}>
                  <td style={{ textAlign: 'center', paddingLeft: '15px' }}>{index + 1}</td>
                  <td style={{ textAlign: 'center' }}>{item.payee?.displayInfo?.title}</td>
                  <td style={{ textAlign: 'center', width: '25px' }}>{item.currency}</td>
                  <td style={{ textAlign: 'center', width: '100px' }}>
                    <SelectInput
                      name="paymentMethodCode"
                      data={Object.values(PaymentType).map((it) => ({
                        id: it,
                        title: translate(it),
                      }))}
                      disabled={
                        isAnyClaimJobItemPaid ||
                        isPaymentMethodLocked ||
                        !(isClaimAdmin || (isClaimOwner && permissions?.EDIT_CLAIMJOBITEM))
                      }
                      value={
                        item.paymentMethodCode ??
                        item.payee?.payee?.paymentMethodCode ??
                        item.payee?.dealer?.paymentMethodCode ??
                        item.payee?.client?.paymentMethodCode
                      }
                      onChange={({ paymentMethodCode }) =>
                        onUpdate({
                          paymentMethodCode: paymentMethodCode,
                          claimJobItemPayee: item.payee,
                        })
                      }
                      error={
                        !(
                          item.paymentMethodCode ??
                          item.payee?.payee?.paymentMethodCode ??
                          item.payee?.dealer?.paymentMethodCode ??
                          item.payee?.client?.paymentMethodCode
                        ) && lang.required
                      }
                      style={{ input: { textAlign: 'center', fontSize: Theme.FontSize.S } }}
                    />
                  </td>
                  <td style={{ textAlign: 'center' }}>{formatNumberWithSeparator(item.parts.toFixed(2))}</td>
                  <td style={{ textAlign: 'center' }}>{formatNumberWithSeparator(item.labour.toFixed(2))}</td>
                  <td style={{ textAlign: 'center' }}>{formatNumberWithSeparator(item.other.toFixed(2))}</td>
                  <td style={{ textAlign: 'center' }}>{formatNumberWithSeparator(item.ttl.toFixed(2))}</td>
                  <td style={{ textAlign: 'center' }}>{formatNumberWithSeparator(item.gstHstRounded.toFixed(2))}</td>
                  {currency === 'CAD' && (
                    <td style={{ textAlign: 'center' }}>
                      {formatNumberWithSeparator(item.provincialSalesTaxRounded.toFixed(2))}
                    </td>
                  )}
                  <td style={{ textAlign: 'center' }}>
                    {formatNumberWithSeparator(item.totalTaxAmountRounded.toFixed(2))}
                  </td>
                  <td style={{ textAlign: 'center' }}>{formatNumberWithSeparator(item.deduct.toFixed(2))}</td>
                  <td style={{ textAlign: 'center' }}>{formatNumberWithSeparator(item.totalRounded.toFixed(2))}</td>
                  <td style={{ textAlign: 'center', padding: '12px 3px' }}>
                    {formatNumberWithSeparator(item.paidAmount.toFixed(2))}
                  </td>
                </tr>
              );
            }}
          />
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={4} style={{ textAlign: 'right', fontWeight: 'bold' }}>
              {lang.currentTotal}
            </td>
            <td style={{ textAlign: 'center' }}>
              {formatNumberWithSeparator(sumBy(aggregates, (it) => it.parts).toFixed(2))}
            </td>
            <td style={{ textAlign: 'center' }}>
              {formatNumberWithSeparator(sumBy(aggregates, (it) => it.labour).toFixed(2))}
            </td>
            <td style={{ textAlign: 'center' }}>
              {formatNumberWithSeparator(sumBy(aggregates, (it) => it.other).toFixed(2))}
            </td>
            <td style={{ textAlign: 'center' }}>
              {formatNumberWithSeparator(sumBy(aggregates, (it) => it.ttl).toFixed(2))}
            </td>
            <td style={{ textAlign: 'center' }}>
              {formatNumberWithSeparator(sumBy(aggregates, (it) => it.gstHstRounded).toFixed(2))}
            </td>
            {currency === 'CAD' && (
              <td style={{ textAlign: 'center' }}>
                {formatNumberWithSeparator(sumBy(aggregates, (it) => it.provincialSalesTaxRounded).toFixed(2))}
              </td>
            )}
            <td style={{ textAlign: 'center' }}>
              {formatNumberWithSeparator(sumBy(aggregates, (it) => it.totalTaxAmountRounded).toFixed(2))}
            </td>
            <td style={{ textAlign: 'center' }}>
              {formatNumberWithSeparator(sumBy(aggregates, (it) => it.deduct).toFixed(2))}
            </td>
            <td style={{ textAlign: 'center' }}>
              {formatNumberWithSeparator(sumBy(aggregates, (it) => it.totalRounded).toFixed(2))}
            </td>
            <td style={{ textAlign: 'center', paddingRight: '3px', padding: '12px 3px' }}>
              {formatNumberWithSeparator(sumBy(aggregates, (it) => it.paidAmount).toFixed(2))}
            </td>
          </tr>
        </tfoot>
      </table>
    </>
  );
}
